import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

const SIZE = { sm: 'p-1', md: 'p-2' } as const;

const VARIANT = {
  ghost: 'btn-ghost',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
} as const;

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Required by the type, not merely allowed. An icon-only control has no
   * accessible name otherwise, and this is the a11y rule most often skipped —
   * the type system can enforce it for free.
   */
  'aria-label': string;
  size?: keyof typeof SIZE;
  variant?: keyof typeof VARIANT;
  loading?: boolean;
}

export function IconButton({
  size = 'md',
  variant = 'ghost',
  loading = false,
  type = 'button',
  disabled,
  className,
  children,
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(VARIANT[variant], SIZE[size], className)}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : children}
    </button>
  );
}
